/**
 * Логика структуры компании (Блок 2): анти-дубли отделов/должностей и группировка строк таблиц
 * по подразделениям. Чистые функции — тестируются изолированно (см. structure.test.ts).
 * Производного признака «несовершеннолетний» здесь больше нет: после centrika-docs!93 он
 * ХРАНИТСЯ в профиле сотрудника и приходит с сервера (`Employee.isMinor`), из даты рождения не
 * выводится.
 */
import type {
  ImportAnalysisColumn,
  ImportArrangementSummary,
  ImportColumnField,
  ImportGroupRole,
  ImportPreview,
  ImportRowDecision,
  Suggestion,
} from './api'

/**
 * Нормализация названия для сравнения «на дубль»: нижний регистр, удаление всего, кроме
 * букв (рус/лат) и цифр. Так «Цех 1», «Цех №1» и «Цех1» считаются одинаковыми.
 */
export function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-zа-яё0-9]/gi, '')
}

/** true, если введённое имя совпадает (после нормализации) с одним из существующих. */
export function isDuplicateName(value: string, existing: string[]): boolean {
  const norm = normalizeName(value)
  if (!norm) return false
  return existing.some((e) => normalizeName(e) === norm)
}

/**
 * Находит существующую подсказку, близкую к введённому значению (точное совпадение после
 * нормализации). Используется, чтобы переиспользовать существующую запись вместо создания дубля.
 */
export function findDuplicateSuggestion(
  value: string,
  suggestions: Suggestion[],
): Suggestion | undefined {
  const norm = normalizeName(value)
  if (!norm) return undefined
  return suggestions.find((s) => normalizeName(s.value) === norm)
}

/**
 * Фильтрует подсказки от сервера: отбрасывает точное (после нормализации) совпадение с уже
 * введённым значением (его и так видно) и дубли по нормализованному значению, сохраняя порядок.
 */
export function dedupeSuggestions(suggestions: Suggestion[]): Suggestion[] {
  const seen = new Set<string>()
  const result: Suggestion[] = []
  for (const s of suggestions) {
    const norm = normalizeName(s.value)
    if (!norm || seen.has(norm)) continue
    seen.add(norm)
    result.push(s)
  }
  return result
}

/** Название группы для строк без подразделения — как в остальных таблицах кабинета. */
export const NO_DEPARTMENT = 'Без подразделения'

/**
 * Группирует строки по подразделению для таблиц с групп-строками: группы — по алфавиту
 * подразделений, «Без подразделения» (`emptyGroup`) — последней (`emptyFirst` — первой: там, где
 * подразделение нужно указать, например на шаге «Заполнение данных»); внутри группы порядок строк
 * сохраняется.
 */
export function groupByDepartment<T>(
  items: T[],
  departmentOf: (item: T) => string,
  emptyGroup: string = NO_DEPARTMENT,
  emptyFirst = false,
): Array<{ department: string; items: T[] }> {
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const department = departmentOf(item).trim() || emptyGroup
    const group = groups.get(department)
    if (group) group.push(item)
    else groups.set(department, [item])
  }
  const emptyOrder = emptyFirst ? -1 : 1
  return Array.from(groups, ([department, grouped]) => ({ department, items: grouped })).sort(
    (a, b) =>
      (Number(a.department === emptyGroup) - Number(b.department === emptyGroup)) * emptyOrder ||
      a.department.localeCompare(b.department, 'ru'),
  )
}

// --- Импорт структуры из своего файла (structure-import-custom-file) ---

/** Отказ для файла неподдерживаемого формата — дословно как на сервере. */
export const STRUCTURE_IMPORT_FORMAT_ERROR =
  'Загрузите файл Excel (.xlsx, .xls), Word (.docx) или PDF с текстом (не скан).'

/** Расширения файлов импорта структуры (structure-import-formats). */
export const STRUCTURE_IMPORT_EXTENSIONS = ['.xlsx', '.xls', '.docx', '.pdf'] as const

/** true — файл можно загрузить в импорт структуры (по расширению; содержимое проверит сервер). */
export function isStructureImportFile(fileName: string): boolean {
  const name = fileName.toLowerCase()
  return STRUCTURE_IMPORT_EXTENSIONS.some((ext) => name.endsWith(ext))
}

/** Разметка своего файла: номер колонки листа → поле. */
export type StructureImportMapping = Record<number, ImportColumnField>

/** Начальная разметка — поля, распознанные сервером. */
export function initialStructureImportMapping(
  columns: ImportAnalysisColumn[],
): StructureImportMapping {
  const out: StructureImportMapping = {}
  for (const c of columns) {
    if (c.field) out[c.index] = c.field
  }
  return out
}

/**
 * Назначает колонке поле (`''` — колонка не загружается). Одно поле — одна колонка: если поле уже
 * было у другой колонки, там оно снимается.
 */
export function assignStructureImportField(
  mapping: StructureImportMapping,
  index: number,
  field: ImportColumnField | '',
): StructureImportMapping {
  const out: StructureImportMapping = {}
  for (const [key, value] of Object.entries(mapping)) {
    if (Number(key) !== index && value !== field) out[Number(key)] = value
  }
  if (field) out[index] = field
  return out
}

/**
 * true — подразделение в файле взять неоткуда: нет ни колонки подразделения, ни строк-заголовков в
 * роли подразделения, — и нужно «Подразделение для всех строк» (structure-import-formats).
 */
export function structureImportNeedsDefaultDepartment(
  mapping: StructureImportMapping,
  groupRole?: ImportGroupRole | null,
): boolean {
  return !Object.values(mapping).includes('department') && groupRole !== 'department'
}

/**
 * Чего не хватает в разметке — зеркало серверной проверки (`imports.checkMapping`); `null` — можно
 * продолжать. Обязательны должность, ФИО (одной колонкой или фамилия + имя; у штатного расписания
 * вместо ФИО — штатные единицы) и подразделение (колонкой или для всех строк).
 */
export function structureImportMappingIssue(
  mapping: StructureImportMapping,
  defaultDepartment: string,
  groupRole?: ImportGroupRole | null,
): string | null {
  const fields = new Set(Object.values(mapping))
  if (!fields.has('position') && groupRole !== 'position') return 'Укажите колонку с должностью.'
  const parts = fields.has('lastName') || fields.has('firstName') || fields.has('middleName')
  if (fields.has('fullName') && parts) {
    return 'ФИО указано и одной колонкой, и по частям — оставьте один вариант.'
  }
  if (parts && !(fields.has('lastName') && fields.has('firstName'))) {
    return 'Укажите колонки с фамилией и именем.'
  }
  // Без ФИО — только штатное расписание: строки дают штатные единицы (structure-import-staffing).
  if (!fields.has('fullName') && !parts && !fields.has('staffUnits')) {
    return 'Укажите колонку с ФИО, колонки с фамилией и именем или колонку со штатными единицами.'
  }
  if (!fields.has('department') && groupRole !== 'department' && !defaultDepartment.trim()) {
    return 'Укажите колонку с подразделением или подразделение для всех строк.'
  }
  return null
}

/**
 * Сколько вакантных мест добавит импорт в каждой паре штатных единиц (structure-import-staffing):
 * повторяет подтверждение на сервере — применяет строки по решениям (создание добавляет сотрудника в
 * пару, обновление переносит его из текущей пары), затем деактивацию, и добирает пары до единиц
 * файла. Лишние единицы не удаляются. Ключ — `ImportStaffingPair.key`.
 */
export function staffingVacanciesToAdd(
  preview: Pick<ImportPreview, 'rows' | 'staffing' | 'deactivateCandidates' | 'personPairs'>,
  decisions: Record<number, ImportRowDecision>,
  deactivateConfirmed: boolean,
): Record<string, number> {
  const units = new Map<string, number>()
  for (const pair of preview.staffing ?? []) units.set(pair.key, pair.currentUnits)
  const shift = (key: string | undefined, delta: number) => {
    if (key && units.has(key)) units.set(key, (units.get(key) ?? 0) + delta)
  }

  const updated = new Set<string>()
  for (const row of preview.rows) {
    const decision = decisions[row.rowNumber]
    const plan =
      row.action === 'update'
        ? 'update'
        : row.action === 'create'
          ? 'create'
          : row.action === 'warning'
            ? decision === 'create'
              ? 'create'
              : 'skip'
            : decision === 'create' || (decision === 'update' && row.suggestedPersonId)
              ? decision
              : 'skip'
    if (plan === 'create') {
      shift(row.createPairKey, 1)
    } else if (plan === 'update') {
      const personId = row.matchedPersonId ?? row.suggestedPersonId
      if (personId) updated.add(personId)
      if (row.personPairKey !== row.updatePairKey) {
        shift(row.personPairKey, -1)
        shift(row.updatePairKey, 1)
      }
    }
  }
  if (deactivateConfirmed) {
    for (const c of preview.deactivateCandidates) {
      if (!updated.has(c.personId)) shift(preview.personPairs?.[c.personId], -1)
    }
  }

  const out: Record<string, number> = {}
  for (const pair of preview.staffing ?? []) {
    out[pair.key] = Math.max(0, pair.fileUnits - (units.get(pair.key) ?? 0))
  }
  return out
}

/** Сводка распознанного отчёта 1С «Штатная расстановка» над предпросмотром. */
export function arrangementNotice(summary: ImportArrangementSummary): string {
  return `Распознан отчёт 1С «Штатная расстановка»: подразделений — ${summary.departments}, должностей — ${summary.positions}, сотрудников — ${summary.persons}, вакантных единиц — ${summary.vacancies}.`
}
